/**
 * Deep-link zoom target for the overlay viewer (`?zoomTo=x0,y0,x1,y1`, normalized [0,1]
 * on the base sheet — self-contained, no page-dimension lookup needed).
 *
 * The overlay viewer's content space is the base sheet's canvas (bW x bH px for the whole page),
 * shared by both sheets under one react-zoom-pan-pinch transform — so centering the target there
 * centers it on both sheets at once.
 */

import {
    parseOverlayLayers,
    setOverlayLayerParams,
    type OverlayLayerRef,
} from "./overlayLayerRefs";
import type { SheetTransform } from "./overlayTypes";

export type ZoomToBounds = [number, number, number, number]; // normalized [0,1] on the base sheet

/** Stable key emitted by `anchorsForSheet`: `<alert_id>#<anchor_index>`. */
export function parseClashFocusParam(raw: string | null): string | null {
    if (!raw || raw.length > 256) return null;
    return /^al-[A-Za-z0-9_.:+-]+#\d+$/.test(raw) ? raw : null;
}

export interface OverlayDeepLinkParams {
    /** The layers drawn over the base sheet, bottom first; empty = no overlay. */
    overlayLayers: readonly OverlayLayerRef[];
    zoomToRaw: string | null;
    zoomTo: ZoomToBounds | null;
    clashFocusKey: string | null;
    /** `?overlayTransform=sx,sy,dx,dy[,quarter_turns,mirrored]`: the overlay's
     *  known placement of the FIRST layer in the `/overlay/align` contract (see
     *  {@link parseOverlayTransformParam}). Null = the viewer solves its own. */
    overlayTransformRaw?: string | null;
    overlayTransform?: SheetTransform | null;
    targetKey: string | null;
}

export function overlayDeepLinkTargetKey(
    target: Pick<
        OverlayDeepLinkParams,
        "overlayLayers" | "zoomToRaw" | "clashFocusKey" | "overlayTransformRaw"
    >,
): string | null {
    if (target.overlayLayers.length === 0) return null;
    return JSON.stringify([
        target.overlayLayers.map((layer) => [layer.fileId, layer.sectionGroupId ?? ""]),
        target.zoomToRaw ?? "",
        target.clashFocusKey ?? "",
        target.overlayTransformRaw ?? "",
    ]);
}

/** Whether a query-only navigation names a target the current or closing overlay has not consumed. */
export function isNewOverlayDeepLink(
    target: OverlayDeepLinkParams,
    activeTargetKey: string | null,
    lastConsumedTargetKey: string | null,
): boolean {
    return (
        target.targetKey !== null &&
        target.targetKey !== activeTargetKey &&
        target.targetKey !== lastConsumedTargetKey
    );
}

/** The URL state restored by both an initial viewer mount and a late query-only navigation. */
/**
 * The write side of {@link parseOverlayDeepLinkParams}: append an overlay deep
 * link to a sheet route. Every producer goes through here so the param names
 * stay in one place, and so appending to a route that already carries a query
 * (a `?hl=` highlight, say) joins with `&` instead of a second `?`.
 *
 * `layers` are the sheets drawn over the base, bottom first, each by its own file id and (when
 * known) the section group that pins its revision. `overlayTransform` places the first layer.
 */
export function withOverlayDeepLink(
    sheetUrl: string,
    layers: readonly OverlayLayerRef[],
    zoomTo?: ZoomToBounds | string | null,
    overlayTransform?: SheetTransform | null,
): string {
    const params = new URLSearchParams();
    setOverlayLayerParams(params, layers);
    // Producers hand this over either as bounds or as the backend's own
    // pre-formatted "x0,y0,x1,y1"; `parseZoomToParam` reads both back.
    if (zoomTo) {
        params.set(
            "zoomTo",
            typeof zoomTo === "string" ? zoomTo : zoomTo.map((v) => v.toFixed(5)).join(","),
        );
    }
    if (overlayTransform) {
        params.set("overlayTransform", formatOverlayTransformParam(overlayTransform));
    }
    // The commas in a bounds tuple and the colon between a layer's file and its
    // revision are legal unencoded in a query value, and these links get pasted
    // into tickets — keep them readable.
    const query = params.toString().replace(/%2C/g, ",").replace(/%3A/g, ":");
    return `${sheetUrl}${sheetUrl.includes("?") ? "&" : "?"}${query}`;
}

export function parseOverlayDeepLinkParams(
    searchParams: Pick<URLSearchParams, "get" | "getAll">,
): OverlayDeepLinkParams {
    const overlayLayers = parseOverlayLayers(searchParams);
    const zoomToRaw = searchParams.get("zoomTo");
    const clashFocusKey = parseClashFocusParam(searchParams.get("clashFocus"));
    const overlayTransformRaw = searchParams.get("overlayTransform");
    const target = {
        overlayLayers,
        zoomToRaw,
        zoomTo: parseZoomToParam(zoomToRaw),
        clashFocusKey,
        overlayTransformRaw,
        overlayTransform: parseOverlayTransformParam(overlayTransformRaw),
    };
    return { ...target, targetKey: overlayDeepLinkTargetKey(target) };
}

/** Beyond this the value cannot be a sheet-onto-sheet placement, only a malformed link. */
const MAX_OVERLAY_TRANSFORM_MAGNITUDE = 100;

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

/**
 * An overlay placement in the `/overlay/align` contract, or null: the overlay
 * page onto the base page, each normalized to [0,1]² by its own size —
 * mirror source x first, then turn clockwise about the origin, then scale
 * output axes and translate. With no orientation this is the aspect-aware
 * `GET /overlay/align` transform. Null unless all four are finite numbers,
 * both scales positive, orientation valid, and
 * nothing is absurdly large.
 */
export function overlayTransformOf(value: unknown): SheetTransform | null {
    if (typeof value !== "object" || value === null) return null;
    const { sx, sy, dx, dy, quarter_turns, mirrored } = value as Partial<
        Record<keyof SheetTransform, unknown>
    >;
    if (!isFiniteNumber(sx) || !isFiniteNumber(sy) || !isFiniteNumber(dx) || !isFiniteNumber(dy)) {
        return null;
    }
    if ([sx, sy, dx, dy].some((v) => Math.abs(v) > MAX_OVERLAY_TRANSFORM_MAGNITUDE)) return null;
    if (sx <= 0 || sy <= 0) return null;
    if (
        quarter_turns !== undefined &&
        (!Number.isInteger(quarter_turns) ||
            (quarter_turns as number) < 0 ||
            (quarter_turns as number) > 3)
    )
        return null;
    if (mirrored !== undefined && typeof mirrored !== "boolean") return null;
    return {
        sx,
        sy,
        dx,
        dy,
        ...(quarter_turns !== undefined
            ? { quarter_turns: quarter_turns as SheetTransform["quarter_turns"] }
            : {}),
        ...(mirrored !== undefined ? { mirrored } : {}),
    };
}

/** Four legacy fields, or `sx,sy,dx,dy,quarter_turns,mirrored` (mirror is 0 or 1). */
export function parseOverlayTransformParam(raw: string | null | undefined): SheetTransform | null {
    if (!raw) return null;
    const parts = raw.split(",").map((part) => (part.trim() === "" ? Number.NaN : Number(part)));
    if (parts.length !== 4 && parts.length !== 6) return null;
    const [sx, sy, dx, dy, quarter_turns, mirror] = parts;
    if (parts.length === 6 && mirror !== 0 && mirror !== 1) return null;
    return overlayTransformOf({
        sx,
        sy,
        dx,
        dy,
        ...(parts.length === 6 ? { quarter_turns, mirrored: mirror === 1 } : {}),
    });
}

/** The `?overlayTransform=` spelling {@link parseOverlayTransformParam} reads: six
 *  decimals (a millionth of the base page), trailing zeros dropped. */
export function formatOverlayTransformParam(transform: SheetTransform): string {
    const fields = [transform.sx, transform.sy, transform.dx, transform.dy].map((value) =>
        String(Number(value.toFixed(6))),
    );
    if (transform.quarter_turns !== undefined || transform.mirrored !== undefined) {
        fields.push(String(transform.quarter_turns ?? 0), transform.mirrored ? "1" : "0");
    }
    return fields.join(",");
}

/** The `?overlayTransform=` the URL keeps: the opener's placement of its first layer, only
 *  while that exact revision (same file AND same section group) is still the stack's first,
 *  so a picker switch to another revision of the file drops it. */
export function overlayTransformParamFor(
    opened: { overlayLayers: readonly OverlayLayerRef[]; overlayTransform?: SheetTransform | null },
    active: readonly OverlayLayerRef[],
): string | null {
    const [openedFirst] = opened.overlayLayers;
    const [activeFirst] = active;
    const sameSheet =
        !!openedFirst &&
        !!activeFirst &&
        openedFirst.fileId === activeFirst.fileId &&
        (openedFirst.sectionGroupId ?? null) === (activeFirst.sectionGroupId ?? null);
    return opened.overlayTransform && sameSheet
        ? formatOverlayTransformParam(opened.overlayTransform)
        : null;
}

/** How much of the viewport the target should occupy (the rest is context). */
const TARGET_VIEWPORT_FRACTION = 0.4;
const MAX_SCALE = 10; // TransformWrapper's maxScale

export function parseZoomToParam(raw: string | null): ZoomToBounds | null {
    if (!raw) return null;
    const parts = raw.split(",").map(Number);
    if (parts.length !== 4 || parts.some((v) => !Number.isFinite(v))) return null;
    // Normalized page coordinates: anything wildly outside [0,1] is a malformed link.
    if (parts.some((v) => v < -0.5 || v > 1.5)) return null;
    return [parts[0], parts[1], parts[2], parts[3]];
}

/**
 * The `?zoomTo=` value for a normalized box, in the spelling `parseZoomToParam` reads
 * back. Five decimals: enough to place a small detail on a large sheet, short enough
 * that the link stays readable.
 */
export function formatZoomToParam(bounds: ZoomToBounds): string {
    return bounds.map((value) => value.toFixed(5)).join(",");
}

/** The TransformWrapper state (translate + scale) that centers the target in the wrapper. */
export function zoomToTransform(
    bounds: ZoomToBounds,
    contentWidth: number,
    contentHeight: number,
    wrapperWidth: number,
    wrapperHeight: number,
): { x: number; y: number; scale: number } {
    const [nx0, ny0, nx1, ny1] = bounds;
    const boxWidth = Math.max((nx1 - nx0) * contentWidth, 1);
    const boxHeight = Math.max((ny1 - ny0) * contentHeight, 1);
    const scale = Math.min(
        (wrapperWidth * TARGET_VIEWPORT_FRACTION) / boxWidth,
        (wrapperHeight * TARGET_VIEWPORT_FRACTION) / boxHeight,
        MAX_SCALE,
    );
    const centerX = ((nx0 + nx1) / 2) * contentWidth;
    const centerY = ((ny0 + ny1) / 2) * contentHeight;
    return {
        x: wrapperWidth / 2 - scale * centerX,
        y: wrapperHeight / 2 - scale * centerY,
        scale,
    };
}
